import { useState } from 'react'
import { Search, User, FileText, DollarSign, Network, History } from 'lucide-react'
import api from '../api/client'

const money = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
const dt = (s) => (s ? new Date(s).toLocaleString('pt-BR') : '—')

export default function BuscaAdmin() {
  const [q, setQ] = useState('')
  const [resultados, setResultados] = useState(null)
  const [dossie, setDossie] = useState(null)
  const [busy, setBusy] = useState(false)

  async function buscar() {
    if (!q.trim()) return
    setBusy(true); setDossie(null)
    try { const r = await api.get(`/busca/clientes?q=${encodeURIComponent(q.trim())}`); setResultados(r.data.data || []) }
    catch { setResultados([]) } finally { setBusy(false) }
  }
  async function abrir(id) {
    setBusy(true)
    try { const r = await api.get(`/busca/clientes/${id}`); setDossie(r.data) }
    catch { /* noop */ } finally { setBusy(false) }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-heading font-bold text-gray-900">Busca Administrativa</h1>
        <p className="text-sm text-gray-500 mt-0.5">Localize por CPF/CNPJ, ID, nome, e-mail, telefone, nº de instalação ou licenciado responsável.</p>
      </div>

      <div className="flex items-center gap-2 max-w-xl">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && buscar()}
            placeholder="CPF, nome, instalação, licenciado..."
            className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2 text-sm outline-none focus:border-accent-400" />
        </div>
        <button onClick={buscar} className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2 text-sm font-semibold">Buscar</button>
      </div>

      {resultados && !dossie && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {resultados.length === 0 && <p className="px-4 py-8 text-center text-gray-400 text-sm">Nenhum cliente encontrado.</p>}
          {resultados.map((c) => (
            <button key={c.id} onClick={() => abrir(c.id)} className="w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-primary-50/40 flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">{c.nome} <span className="text-xs text-gray-400">#{c.id}</span></p>
                <p className="text-xs text-gray-500">{c.cpf_cnpj || '—'} · {c.email || '—'} · {c.telefone || '—'}</p>
              </div>
              <span className="text-xs text-gray-500">Lic: {c.licenciado_nome || '—'}</span>
            </button>
          ))}
        </div>
      )}

      {dossie && (
        <div className="space-y-4">
          <button onClick={() => setDossie(null)} className="text-sm text-accent-600 hover:underline">← voltar aos resultados</button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wide mb-2"><User size={14} /> Cliente</div>
              <p className="font-heading font-bold text-gray-900">{dossie.cliente.nome}</p>
              <p className="text-sm text-gray-600 mt-1">{dossie.cliente.cpf_cnpj || '—'}</p>
              <p className="text-sm text-gray-600">{dossie.cliente.email || '—'}</p>
              <p className="text-sm text-gray-600">{dossie.cliente.telefone || '—'}</p>
              <p className="text-sm text-gray-600">{[dossie.cliente.cidade, dossie.cliente.estado].filter(Boolean).join(' / ') || '—'}</p>
              {dossie.cliente.numero_instalacao && <p className="text-xs text-gray-500 mt-1">Instalação: {dossie.cliente.numero_instalacao}</p>}
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wide mb-2"><Network size={14} /> Vínculos</div>
              <p className="text-sm"><b>Licenciado:</b> {dossie.licenciado?.nome || '—'}</p>
              <p className="text-sm"><b>Parceiro:</b> {dossie.parceiro?.nome || '—'}</p>
              <p className="text-sm"><b>Produto/Serviço:</b> {dossie.oferta?.nome || '—'}</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wide mb-2"><DollarSign size={14} /> Recorrência</div>
              <p className="text-sm"><b>Faturas pagas:</b> {dossie.recorrencia.qtd_pagas}</p>
              <p className="text-sm"><b>Total pago:</b> {money(dossie.recorrencia.total_pago)}</p>
              <p className="text-sm"><b>PV total:</b> {dossie.recorrencia.pv_total}</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 text-gray-700 font-semibold mb-3"><FileText size={16} /> Faturas ({dossie.faturas.length})</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm whitespace-nowrap">
                <thead className="text-gray-400 text-xs uppercase text-left"><tr><th className="py-1 pr-4">Ref.</th><th className="py-1 pr-4">Valor</th><th className="py-1 pr-4">Status</th><th className="py-1 pr-4">PV</th><th className="py-1 pr-4">Comissão</th><th className="py-1 pr-4">Pago em</th></tr></thead>
                <tbody>
                  {dossie.faturas.map((f) => (
                    <tr key={f.id} className="border-t border-gray-50">
                      <td className="py-1.5 pr-4">{f.competencia}</td><td className="py-1.5 pr-4">{money(f.valor)}</td>
                      <td className="py-1.5 pr-4">{f.status}</td><td className="py-1.5 pr-4">{f.pv_gerado ?? '—'}</td>
                      <td className="py-1.5 pr-4">{money(f.comissao_total)}</td><td className="py-1.5 pr-4 text-gray-500">{dt(f.pago_em)}</td>
                    </tr>
                  ))}
                  {dossie.faturas.length === 0 && <tr><td colSpan={6} className="py-4 text-gray-400">Sem faturas.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 text-gray-700 font-semibold mb-3"><DollarSign size={16} /> Comissões ({dossie.comissoes.length})</div>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {dossie.comissoes.map((k) => (
                  <div key={k.id} className="flex items-center justify-between text-sm border-b border-gray-50 pb-1">
                    <span className="text-gray-600">{k.tipo} · {k.mes_referencia || '—'} {k.status === 'estornado' && <span className="text-rose-500">(estornado)</span>}</span>
                    <span className="font-medium">{money(k.valor)}</span>
                  </div>
                ))}
                {dossie.comissoes.length === 0 && <p className="text-sm text-gray-400">Sem comissões.</p>}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 text-gray-700 font-semibold mb-3"><History size={16} /> Histórico de alterações</div>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {dossie.historico.map((h, i) => (
                  <div key={i} className="text-xs border-b border-gray-50 pb-1">
                    <span className="text-gray-400">{dt(h.created_at)}</span> · <b>{h.acao}</b> — {h.detalhe} <span className="text-gray-400">({h.usuario_nome})</span>
                  </div>
                ))}
                {dossie.historico.length === 0 && <p className="text-sm text-gray-400">Sem histórico.</p>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
